import { useState } from 'react';
import type { Programme } from '../types';
import { updateProgramme, deleteProgramme } from '../lib/api';
import { IconClose, IconCheck, IconRefresh } from '../components/icons';
import { useToast } from '../lib/toast';
import { BaseDrawer } from '../components/BaseDrawer';

interface Props {
  programme: Programme;
  onClose: () => void;
  onSaved: (p: Programme) => void;
  onDeleted: (id: string) => void;
}

const PUBLI_STATUS = ['Draft', 'Publié', 'Archivé', 'Rejeté'];
const CONFIDENCE = ['Élevée', 'Moyenne', 'Faible'];
const COUNTRIES = ['Canada', 'United States', 'France', 'United Kingdom', 'European Union', 'Other'];
const INSTRUMENT_TYPES = ['Subvention', 'Prêt', 'Crédit d\'impôt', 'Garantie', 'Bourse', 'Avance remboursable', 'Mixte'];
const CADENCES = ['Permanente', 'Annuelle', 'Trimestrielle', 'Ponctuelle', 'Sur appel'];
const STAGES_OPTIONS = ['Recherche fondamentale', 'Pre-seed', 'Seed', 'Series A', 'Growth', 'Commercialisation'];
const RATE_TYPES = ['Pourcentage', 'Forfait', 'Mixte', 'Variable'];
const CUMULABLE = ['Oui', 'Non', 'Partiellement', 'À évaluer'];
const ACCESS_MODES = ['Continu', 'Appel à projets', 'Procédure d\'agrément', 'Guichet unique'];
const INCORPORATION = ['Oui', 'Non', 'Selon entité'];

/**
 * Drawer d'édition de programme non-dilutif. Slide-in panel 540px à droite.
 *
 * Pattern simplifié vs InvestorEditDrawer : pas d'autosave Phase 3, save
 * explicite via le bouton. On peut ajouter l'autosave plus tard si besoin.
 *
 * Bilingue obligatoire : Nom EN + Nom FR sont signalés en rouge si vides
 * au moment du Save (cf. P7 brief V2 §13).
 */
export function ProgrammeEditDrawer({ programme, onClose, onSaved, onDeleted }: Props) {
  const [draft, setDraft] = useState<Programme>(programme);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  function setField<K extends keyof Programme>(key: K, value: Programme[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(programme);
  const bilingueIncomplete = !draft.nameEn?.trim() || !draft.nameFr?.trim();

  async function onSave() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const { id, createdTime, ...patch } = draft;
      void id; void createdTime;
      const res = await updateProgramme(programme.id, patch);
      onSaved(res.programme);
      toast.success('Programme sauvegardé', draft.nameFr || draft.nameEn || '(sans nom)');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast.error('Sauvegarde échouée', msg);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!confirm(`Supprimer définitivement "${draft.nameFr || draft.nameEn || programme.id}" ?`)) return;
    setDeleting(true);
    try {
      await deleteProgramme(programme.id);
      onDeleted(programme.id);
      toast.success('Programme supprimé', '');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error('Suppression échouée', msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <BaseDrawer onClose={onClose} widthClass="w-full md:w-[540px] md:max-w-[92vw]">
      {({ close }) => (
        <>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-line-soft px-5 py-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">
              Programme non-dilutif
            </div>
            <div className="text-[15px] font-display tracking-tightish text-ink truncate">
              {draft.nameFr || draft.nameEn || 'Nouveau programme'}
            </div>
          </div>
          <button
            onClick={close}
            className="h-8 w-8 grid place-items-center rounded-md hover:bg-cream-tint transition"
            aria-label="Fermer"
          >
            <IconClose className="w-4 h-4 text-ink-muted" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Identity */}
          <Section title="Identité">
            <Field label="ID programme" hint="format NDQ-0001">
              <input
                value={draft.idProgramme ?? ''}
                onChange={(e) => setField('idProgramme', e.target.value)}
                className="input font-mono text-[12px]"
              />
            </Field>
            <Field label="Nom du programme (FR)" required error={!draft.nameFr?.trim() && dirty ? 'Requis pour parité bilingue' : undefined}>
              <input
                value={draft.nameFr ?? ''}
                onChange={(e) => setField('nameFr', e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Nom du programme (EN)" required error={!draft.nameEn?.trim() && dirty ? 'Required for bilingual parity' : undefined}>
              <input
                value={draft.nameEn ?? ''}
                onChange={(e) => setField('nameEn', e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Slug">
              <input
                value={draft.slug ?? ''}
                onChange={(e) => setField('slug', e.target.value)}
                className="input font-mono text-[12px]"
              />
            </Field>
            <Field label="Opérateur">
              <input
                value={draft.operator ?? ''}
                onChange={(e) => setField('operator', e.target.value)}
                className="input"
              />
            </Field>
            <Row>
              <Field label="Pays">
                <select value={draft.country ?? ''} onChange={(e) => setField('country', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Géographie">
                <input
                  value={draft.geography ?? ''}
                  onChange={(e) => setField('geography', e.target.value)}
                  className="input"
                  placeholder="sous-national, ex Québec"
                />
              </Field>
            </Row>
          </Section>

          {/* Copy bilingue */}
          <Section title="Copy éditoriale">
            <Field label="Résumé (FR)">
              <textarea rows={4} value={draft.summaryFr ?? ''} onChange={(e) => setField('summaryFr', e.target.value)} className="input" />
            </Field>
            <Field label="Résumé (EN)">
              <textarea rows={4} value={draft.summaryEn ?? ''} onChange={(e) => setField('summaryEn', e.target.value)} className="input" />
            </Field>
            <Field label="Éligibilité rédigée (FR)">
              <textarea rows={3} value={draft.eligibilityFr ?? ''} onChange={(e) => setField('eligibilityFr', e.target.value)} className="input" />
            </Field>
            <Field label="Éligibilité rédigée (EN)">
              <textarea rows={3} value={draft.eligibilityEn ?? ''} onChange={(e) => setField('eligibilityEn', e.target.value)} className="input" />
            </Field>
          </Section>

          {/* Montants */}
          <Section title="Montants">
            <Row>
              <Field label="Montant min">
                <input
                  type="number"
                  value={draft.minAmount ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { setField('minAmount', undefined); return; }
                    const n = Number(raw);
                    setField('minAmount', Number.isFinite(n) ? n : undefined);
                  }}
                  className="input num"
                />
              </Field>
              <Field label="Montant max">
                <input
                  type="number"
                  value={draft.maxAmount ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { setField('maxAmount', undefined); return; }
                    const n = Number(raw);
                    setField('maxAmount', Number.isFinite(n) ? n : undefined);
                  }}
                  className="input num"
                />
              </Field>
            </Row>
            <Row>
              <Field label="Type de taux">
                <select value={draft.rateType ?? ''} onChange={(e) => setField('rateType', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {RATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Valeur du taux">
                <input value={draft.rateValue ?? ''} onChange={(e) => setField('rateValue', e.target.value)} className="input" />
              </Field>
            </Row>
            <Field label="Note montant">
              <textarea rows={2} value={draft.amountNote ?? ''} onChange={(e) => setField('amountNote', e.target.value)} className="input" />
            </Field>
            <Row>
              <Field label="Cumulable">
                <select value={draft.cumulable ?? ''} onChange={(e) => setField('cumulable', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {CUMULABLE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </Row>
            {draft.cumulable && draft.cumulable !== 'Non' && (
              <Field label="Note cumul">
                <textarea rows={2} value={draft.cumulableNote ?? ''} onChange={(e) => setField('cumulableNote', e.target.value)} className="input" />
              </Field>
            )}
          </Section>

          {/* Process */}
          <Section title="Process et éligibilité">
            <Row>
              <Field label="Type d'instrument">
                <select value={draft.instrumentType ?? ''} onChange={(e) => setField('instrumentType', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {INSTRUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Cadence">
                <select value={draft.cadence ?? ''} onChange={(e) => setField('cadence', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </Row>
            <Row>
              <Field label="Mode d'accès">
                <select value={draft.accessMode ?? ''} onChange={(e) => setField('accessMode', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {ACCESS_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Incorporation requise">
                <select value={draft.incorporationRequired ?? ''} onChange={(e) => setField('incorporationRequired', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {INCORPORATION.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </Row>
            <Field label="Stade (multi)">
              <MultiSelect options={STAGES_OPTIONS} value={draft.stages ?? []} onChange={(v) => setField('stages', v)} />
            </Field>
            <Field label="Contraintes notables">
              <textarea rows={3} value={draft.notableConstraints ?? ''} onChange={(e) => setField('notableConstraints', e.target.value)} className="input" />
            </Field>
          </Section>

          {/* Meta éditoriale */}
          <Section title="Méta éditoriale">
            <Field label="URL officielle">
              <input
                type="url"
                value={draft.officialUrl ?? ''}
                onChange={(e) => setField('officialUrl', e.target.value)}
                className="input font-mono text-[12px]"
              />
            </Field>
            <Row>
              <Field label="Date de dernière vérif">
                <input
                  type="date"
                  value={draft.lastVerifiedDate ?? ''}
                  onChange={(e) => setField('lastVerifiedDate', e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="Vérifié par">
                <input value={draft.verifiedBy ?? ''} onChange={(e) => setField('verifiedBy', e.target.value)} className="input" />
              </Field>
            </Row>
            <Row>
              <Field label="Confiance">
                <select value={draft.confidence ?? ''} onChange={(e) => setField('confidence', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {CONFIDENCE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Statut de publication">
                <select value={draft.publicationStatus ?? ''} onChange={(e) => setField('publicationStatus', e.target.value)} className="input">
                  <option value="">Non défini</option>
                  {PUBLI_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </Row>
            <Field label="Lien source vivant">
              <label className="inline-flex items-center gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  checked={!!draft.linkAlive}
                  onChange={(e) => setField('linkAlive', e.target.checked)}
                  className="h-4 w-4"
                />
                URL toujours active à la dernière vérif
              </label>
            </Field>
            <Field label="Journal pipeline">
              <textarea rows={3} value={draft.pipelineJournal ?? ''} onChange={(e) => setField('pipelineJournal', e.target.value)} className="input font-mono text-[12px]" />
            </Field>
          </Section>
        </div>

        {/* Footer */}
        <div className="border-t border-line-soft px-5 py-3 flex items-center justify-between gap-2 bg-cream-tint">
          <button
            onClick={onDelete}
            disabled={deleting}
            className="text-[12px] text-red-700 hover:underline disabled:opacity-50"
          >
            {deleting ? 'Suppression...' : 'Supprimer'}
          </button>
          <div className="flex items-center gap-2">
            {bilingueIncomplete && dirty && (
              <span className="text-[11px] text-amber font-medium">Nom FR + EN requis</span>
            )}
            <button
              onClick={close}
              className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition"
            >
              Fermer
            </button>
            <button
              onClick={onSave}
              disabled={!dirty || saving}
              className="h-9 px-3.5 rounded-md bg-navy text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-navy-soft transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {error && (
          <div className="absolute bottom-16 left-5 right-5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            {error}
          </div>
        )}

        {/* Styles inline pour input et num : alignés sur les conventions Tailwind du projet */}
        <style>{`
          .input { width: 100%; padding: 0.4rem 0.6rem; border: 1px solid #E5E7EB; border-radius: 6px; font-size: 13px; background: white; }
          .input:focus { outline: 2px solid #8B5CF6; outline-offset: -1px; border-color: transparent; }
          textarea.input { resize: vertical; min-height: 60px; font-family: inherit; }
          .num { font-variant-numeric: tabular-nums; }
        `}</style>
        </>
      )}
    </BaseDrawer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] uppercase tracking-wider text-ink-faint font-medium mb-2 pb-1 border-b border-line-soft">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, hint, required, error, children }: {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
        {hint && <span className="text-ink-faint normal-case ml-1 tracking-normal">({hint})</span>}
      </label>
      {children}
      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}

function MultiSelect({ options, value, onChange }: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(opt: string) {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={
              'px-2.5 py-1 rounded-full text-[12px] border transition ' +
              (active
                ? 'bg-founders-soft text-founders-deep border-founders/30'
                : 'bg-white text-ink-muted border-line hover:bg-cream-tint')
            }
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
